import { useEffect, useRef, useState } from "react";
import LoadingCircle from "../utility/LoadingCircle";
import ReviewPhotoPreview from "./ReviewPhotoPreview";
import ReviewSummary from "./ReviewSummary";

const VERDICT_LABEL = {
	PASS: "Pass",
	FAIL: "Fail",
	UNABLE_TO_VERIFY: "Unable to verify",
};

const overallVerdict = (inspectionResults) => {
	const verdicts = inspectionResults.map((item) => item.verdict);
	if (verdicts.includes("FAIL")) return "FAIL";
	if (verdicts.includes("UNABLE_TO_VERIFY")) return "UNABLE_TO_VERIFY";
	if (verdicts.includes("PASS")) return "PASS";
	return "";
};

const MAXIMUM_REVIEW_IMAGES = 5;

const VERDICT_STYLE = {
	PASS: "bg-green-100 text-green-800",
	FAIL: "bg-red-100 text-red-800",
	UNABLE_TO_VERIFY: "bg-amber-100 text-amber-900",
};

const VERDICT_EDGE = {
	PASS: "bg-green-600",
	FAIL: "bg-red-600",
	UNABLE_TO_VERIFY: "bg-amber-500",
};

const thumbnailToFile = (thumbnailValue, fileName) => {
	const commaIndex = thumbnailValue.indexOf(",");
	const base64 = thumbnailValue.slice(commaIndex + 1).replace(/\s/g, "");
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index += 1) {
		bytes[index] = binary.charCodeAt(index);
	}
	return new File([bytes], fileName, { type: "image/jpeg" });
};

const AiImageReviewModal = ({
	api,
	token,
	prompt,
	fieldValue,
	comment,
	imageQuery,
	imagePath,
	initialThumbnailPaths,
	knownPhotoPaths,
	projectId,
	fieldId,
	onClose,
}) => {
	const [loading, setLoading] = useState(true);
	const [loadingMessage, setLoadingMessage] = useState("Loading photos…");
	const [errorMessage, setErrorMessage] = useState("");
	const [result, setResult] = useState(null);
	const [reviewedPhotos, setReviewedPhotos] = useState([]);
	const [saveMessage, setSaveMessage] = useState("");
	const [reviewCompletedOn, setReviewCompletedOn] = useState("");
	const [requestedBy, setRequestedBy] = useState("");
	const [availablePhotos, setAvailablePhotos] = useState([]);
	const [selectedPhotoPaths, setSelectedPhotoPaths] = useState([]);
	const [choosingPhotos, setChoosingPhotos] = useState(false);
	const thumbnailPathsRef = useRef(initialThumbnailPaths);
	const knownPhotoPathsRef = useRef(knownPhotoPaths);
	const submitReviewRef = useRef(null);
	thumbnailPathsRef.current = initialThumbnailPaths;
	knownPhotoPathsRef.current = knownPhotoPaths;
	const thumbnailPathKey = [...(initialThumbnailPaths || []).map((thumbnail) => thumbnail?.path_lower || ""), ...(knownPhotoPaths || [])]
		.filter(Boolean)
		.join("|");

	useEffect(() => {
		let cancelled = false;
		const pathsForThisReview = thumbnailPathsRef.current;
		const knownPathsForThisReview = knownPhotoPathsRef.current;

		const reviewImages = async () => {
			if (!api || !token) {
				setErrorMessage("Not signed in or API is unavailable.");
				setLoading(false);
				return;
			}

			try {
				let imagePaths = [...(knownPathsForThisReview || [])];
				if (pathsForThisReview && pathsForThisReview.length > 0) {
					imagePaths = [...imagePaths, ...pathsForThisReview.map((thumbnail) => thumbnail.path_lower).filter(Boolean)];
				}
				const pathResponse = await fetch(api + "/project/dropbox/images", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					body: JSON.stringify({
						query: imageQuery
							.replaceAll("/", "_")
							.replaceAll("\\", "_")
							.replaceAll("<", "_")
							.replaceAll(">", "_")
							.replaceAll(":", "_")
							.replaceAll('"', "_")
							.replaceAll("|", "_")
							.replaceAll("?", "_")
							.replaceAll("*", "_")
							.replaceAll(".", "_"),
						path: imagePath,
					}),
				});
				const pathData = await pathResponse.json().catch(() => ({}));
				if (!pathResponse.ok) {
					throw new Error(pathData.message || pathData.msg || "Unable to load this field's photos.");
				}
				imagePaths = [...new Set([...imagePaths, ...(pathData.matches || []).map((match) => match.metadata?.metadata?.path_lower).filter(Boolean)])];

				const photoPaths = imagePaths.filter((path) => !path.toLowerCase().endsWith(".pdf"));
				if (photoPaths.length === 0) {
					throw new Error("Add a photo to this field before reviewing images.");
				}

				const thumbnailResponse = await fetch(api + "/project/dropbox/thumbnails", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					body: JSON.stringify({ entries: photoPaths }),
				});
				const thumbnailData = await thumbnailResponse.json().catch(() => ({}));
				if (!thumbnailResponse.ok || !thumbnailData.thumbnail?.entries) {
					throw new Error(thumbnailData.message || thumbnailData.msg || "Unable to load this field's photos.");
				}

				const loadedPhotos = thumbnailData.thumbnail.entries
					.map((entry, photoIndex) => {
						if (entry[".tag"] === "failure" || !entry.thumbnail) return null;
						const fileName = entry.metadata?.name || `image-${photoIndex + 1}.jpg`;
						const thumbnailValue = "data:image/jpeg;base64," + entry.thumbnail;
						return {
							path: entry.metadata?.path_lower || photoPaths[photoIndex] || fileName,
							fileName,
							thumbnailValue,
							serverModified: entry.metadata?.server_modified || null,
						};
					})
					.filter(Boolean);

				if (loadedPhotos.length === 0) {
					throw new Error("Unable to load this field's photos.");
				}
				if (cancelled) return;

				if (loadedPhotos.length > MAXIMUM_REVIEW_IMAGES) {
					setAvailablePhotos(loadedPhotos);
					setSelectedPhotoPaths([]);
					setChoosingPhotos(true);
					setLoading(false);
					return;
				}

				await submitReview(loadedPhotos);
			} catch (error) {
				if (!cancelled) setErrorMessage(error.message || "Unable to review these images.");
				if (!cancelled) setLoading(false);
			}
		};

		const submitReview = async (photosToReview) => {
			setChoosingPhotos(false);
			setLoading(true);
			setLoadingMessage("Reviewing images…");
			setErrorMessage("");
			setResult(null);
			setReviewCompletedOn("");
			setRequestedBy("");
			setReviewedPhotos(photosToReview);
			try {
				const imageFiles = photosToReview.map((photo) => thumbnailToFile(photo.thumbnailValue, photo.fileName));
				const instructionsResponse = await fetch(`${api}/ai/instructions`, {
					headers: {
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
				});
				const instructionsData = await instructionsResponse.json().catch(() => ({}));
				if (!instructionsResponse.ok) {
					throw new Error(instructionsData.msg || instructionsData.error || "Unable to load the saved instructions.");
				}

				const body = new FormData();
				body.append("prompt", prompt.trim());
				body.append("instructions", (instructionsData.instructions || "").trim());
				body.append("fieldValue", (fieldValue || "").trim());
				body.append("comment", (comment || "").trim());
				imageFiles.forEach((imageFile) => body.append("images", imageFile, imageFile.name));

				const reviewResponse = await fetch(`${api}/ai`, {
					method: "POST",
					headers: {
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					body,
				});
				const reviewData = await reviewResponse.json().catch(() => ({}));
				if (!reviewResponse.ok) {
					throw new Error(reviewData.msg || reviewData.error || `Request failed (${reviewResponse.status})`);
				}
				if (!cancelled) {
					setResult(reviewData);
					setReviewCompletedOn(new Date().toISOString());
					setRequestedBy(reviewData.username || "");
				}
				if (!cancelled) {
					await saveReviewHistory(photosToReview, reviewData?.inspection?.results || []);
				}
			} catch (error) {
				if (!cancelled) setErrorMessage(error.message || "Unable to review these images.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		const saveReviewHistory = async (photosToReview, inspectionResults) => {
			if (!projectId || !fieldId) return;
			const photos = photosToReview.map((photo, photoIndex) => {
				const inspectionResult = inspectionResults.find((item) => item.imageIndex === photoIndex + 1) || inspectionResults[photoIndex] || {};
				return {
					path: photo.path,
					serverModified: photo.serverModified || null,
					verdict: inspectionResult.verdict || "UNABLE_TO_VERIFY",
					explanation: inspectionResult.explanation || "",
					nextSteps: inspectionResult.nextSteps || "",
				};
			});
			try {
				const saveResponse = await fetch(`${api}/ai/reviews`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					body: JSON.stringify({ projectId, fieldId, photos }),
				});
				const saveData = await saveResponse.json().catch(() => ({}));
				if (!saveResponse.ok) {
					if (!cancelled) setSaveMessage(saveData.msg || "This review was not saved to history.");
				} else if (!cancelled && saveData.review) {
					if (saveData.review.reviewedOn) setReviewCompletedOn(saveData.review.reviewedOn);
					if (saveData.review.username) setRequestedBy(saveData.review.username);
				}
			} catch (error) {
				if (!cancelled) setSaveMessage("This review was not saved to history.");
			}
		};

		submitReviewRef.current = submitReview;

		const startTimer = window.setTimeout(() => {
			if (!cancelled) reviewImages();
		}, 0);
		return () => {
			cancelled = true;
			window.clearTimeout(startTimer);
		};
	}, [api, token, prompt, fieldValue, comment, imageQuery, imagePath, thumbnailPathKey]);

	const results = result?.inspection?.results || [];
	const selectedCount = selectedPhotoPaths.length;
	const toggleSelectedPhoto = (photoPath) => {
		setSelectedPhotoPaths((currentPaths) => {
			if (currentPaths.includes(photoPath)) {
				return currentPaths.filter((path) => path !== photoPath);
			}
			if (currentPaths.length >= MAXIMUM_REVIEW_IMAGES) {
				return currentPaths;
			}
			return [...currentPaths, photoPath];
		});
	};
	const reviewSelectedPhotos = () => {
		const photosToReview = availablePhotos.filter((photo) => selectedPhotoPaths.includes(photo.path));
		if (photosToReview.length < 1 || photosToReview.length > MAXIMUM_REVIEW_IMAGES || !submitReviewRef.current) return;
		submitReviewRef.current(photosToReview);
	};

	const reviewVerdict = overallVerdict(results);
	const passedCount = results.filter((item) => item.verdict === "PASS").length;
	const reviewDateLabel = reviewCompletedOn ? new Date(reviewCompletedOn).toLocaleString() : "";

	return (
		<div className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black/50 p-4">
			<div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white">
				<div className="flex items-center justify-between gap-3 border-b border-stone-200 p-4">
					<p className="text-xl font-bold">Review images</p>
					<button type="button" className="rounded bg-gray-500 px-3 py-1 text-white" onClick={onClose}>
						Close
					</button>
				</div>
				<div className="overflow-y-auto p-4">
					{loading ? (
						<div className="mt-8 flex flex-col items-center gap-3 pb-6">
							<LoadingCircle />
							<p className="text-sm text-stone-600">{loadingMessage}</p>
						</div>
					) : null}
					{choosingPhotos && !loading ? (
						<div className="">
							<p className="text-sm text-stone-700">
								This field has {availablePhotos.length} photos. Choose up to 5 to review, or close this window and remove extras before you
								continue.
							</p>
							<p className="mt-2 text-sm font-semibold text-stone-800">{selectedCount} selected, up to 5</p>
							<ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
								{availablePhotos.map((photo) => {
									const selected = selectedPhotoPaths.includes(photo.path);
									return (
										<li key={photo.path}>
											<button
												type="button"
												aria-pressed={selected}
												onClick={() => toggleSelectedPhoto(photo.path)}
												className={`relative w-full overflow-hidden rounded-lg border-4 bg-white ${
													selected ? "border-green-600" : "border-stone-200"
												}`}>
												<img src={photo.thumbnailValue} alt="" className="h-28 w-full object-cover" />
												<span
													className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
														selected ? "border-green-600 bg-green-600 text-white" : "border-white bg-white/80"
													}`}>
													{selected ? (
														<svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
															<path
																d="M5 10.5 8.2 14 15 6.5"
																stroke="currentColor"
																strokeWidth="2.2"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													) : null}
												</span>
											</button>
										</li>
									);
								})}
							</ul>
							<div className="mt-4 flex justify-end">
								<button
									type="button"
									disabled={selectedCount < 1 || selectedCount > MAXIMUM_REVIEW_IMAGES}
									onClick={reviewSelectedPhotos}
									className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
									Review selected
								</button>
							</div>
						</div>
					) : null}
					{errorMessage ? (
						<div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
							<p className="font-semibold">Unable to review images</p>
							<p className="mt-1 whitespace-pre-wrap">{errorMessage}</p>
						</div>
					) : null}
					{!loading && result && results.length > 0 ? (
						<ReviewSummary
							verdict={reviewVerdict}
							passedCount={passedCount}
							totalCount={results.length}
							dateLabel={reviewDateLabel}
							requestedBy={requestedBy}
						/>
					) : null}
					{!loading && result ? (
						<div className="flex flex-col gap-3">
							{results.map((item, index) => {
								const reviewedPhoto = item.imageIndex > 0 ? reviewedPhotos[item.imageIndex - 1] : null;
								return (
									<div
										key={`${item.imageIndex}-${index}`}
										className="relative flex items-start gap-3 overflow-hidden rounded-lg border border-stone-200 p-3 pr-5">
										<span className={`absolute inset-y-0 right-0 w-2 ${VERDICT_EDGE[item.verdict] || "bg-stone-300"}`} />
										{reviewedPhoto?.thumbnailValue ? (
											<ReviewPhotoPreview
												thumbnailValue={reviewedPhoto.thumbnailValue}
												alt={reviewedPhoto.fileName || `Image ${item.imageIndex}`}
											/>
										) : null}
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-semibold text-stone-800">
													{item.imageIndex > 0 ? `Image ${item.imageIndex}` : "No image"}
												</p>
												<span
													className={`rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_STYLE[item.verdict] || "bg-stone-100 text-stone-700"}`}>
													{VERDICT_LABEL[item.verdict] || item.verdict || "—"}
												</span>
											</div>
											{item.explanation ? <p className="mt-2 whitespace-pre-wrap text-sm text-stone-800">{item.explanation}</p> : null}
											{item.nextSteps ? (
												<p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">
													<span className="font-semibold">Next steps: </span>
													{item.nextSteps}
												</p>
											) : null}
										</div>
									</div>
								);
							})}
							{results.length === 0 ? (
								<div className="rounded-lg border border-stone-200 p-4">
									<p className="whitespace-pre-wrap text-sm text-stone-900">{result.response || "No review result was returned."}</p>
								</div>
							) : null}
						</div>
					) : null}
					{saveMessage ? <p className="mt-4 text-sm text-amber-800">{saveMessage}</p> : null}
				</div>
			</div>
		</div>
	);
};

export default AiImageReviewModal;
